import get from "lodash/get";
import has from "lodash/has";
import isArray from "lodash/isArray";
import isNull from "lodash/isNull";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import some from "lodash/some";
import { defineMessages, FormattedMessage, IntlShape } from "react-intl";

import { IListingRules } from "~/types/local-types";

import accessTypes from "./access-type";
import parkingTypes from "./parking-types";

const definedMessages = defineMessages({
  true: {
    id: "listing.production_utils.true",
    defaultMessage: "true",
  },
  false: {
    id: "listing.production_utils.false",
    defaultMessage: "false",
  },
  naturalLight: {
    id: "listing.production_utils.has_light",
    defaultMessage: "Space has natural light",
  },
  noNaturalLight: {
    id: "listing.production_utils.no_light",
    defaultMessage: "Space does not have natural light",
  },
  blockNaturalLight: {
    id: "listing.production_utils.block_light",
    defaultMessage: "Natural light can be blocked out",
  },
  noBlockNaturalLight: {
    id: "listing.production_utils.no_block_light",
    defaultMessage: "Natural light can not be blocked out",
  },
  lightingDescription: {
    id: "listing.production_utils.lighting_description",
    defaultMessage: "Lighting description",
  },
  soundProof: {
    id: "listing.production_utils.sound_proof",
    defaultMessage: "Space is soundproof",
  },
  noSoundProof: {
    id: "listing.production_utils.no_sound_proof",
    defaultMessage: "Space is not soundproof",
  },
  ambientNoiseDescription: {
    id: "listing.production_utils.ambient_noise_description",
    defaultMessage: "Ambient noise description",
  },
  crewAccess: {
    id: "listing.production_utils.crew_access",
    defaultMessage: "Crew access",
  },
  equipmentLoadingDescription: {
    id: "listing.production_utils.equipment_description",
    defaultMessage: "Equipment loading description",
  },
  staging: {
    id: "listing.production_utils.staging",
    defaultMessage: "Staging area is available",
  },
  noStaging: {
    id: "listing.production_utils.no_staging",
    defaultMessage: "No staging area available",
  },
  offLimit: {
    id: "listing.production_utils.off_limit",
    defaultMessage: "Some rooms are off-limits for shooting",
  },
  noOffLimit: {
    id: "listing.production_utils.no_off_limit",
    defaultMessage: "All rooms are available for shooting",
  },
  filmExterior: {
    id: "listing.production_utils.film_exterior",
    defaultMessage: "Shooting the exterior of the building is allowed",
  },
  noFilmExterior: {
    id: "listing.production_utils.no_film_exterior",
    defaultMessage: "Shooting the exterior of the building is not allowed",
  },
  electricalSystemDescription: {
    id: "listing.production_utils.electrical_description",
    defaultMessage: "Electrical system description",
  },
  accessCircuitBreaker: {
    id: "listing.production_utils.access_circuit",
    defaultMessage: "Circuit breaker can be accessed",
  },
  noAccessCircuitBreaker: {
    id: "listing.production_utils.no_access_circuit",
    defaultMessage: "Circuit breaker can not be accessed",
  },
  rules: {
    id: "listing.production_utils.rules",
    defaultMessage: "General Rules",
  },
  minAgeAllowed: {
    id: "listing.production_utils.min_age_allowed",
    defaultMessage: "Ages {answer}+",
  },
  allAgeAllowed: {
    id: "listing.production_utils.all_age_allowed",
    defaultMessage: "All ages are allowed in the space",
  },
  prodInsuranceRequired: {
    id: "listing.production_utils.insurance",
    defaultMessage: "Production insurance is required",
  },
  noProdInsuranceRequired: {
    id: "listing.production_utils.no_insurance",
    defaultMessage: "Production insurance is not required",
  },
  lighting: {
    id: "listing.production_utils.lighting",
    defaultMessage: "Lighting",
  },
  moveFurniture: {
    id: "listing.production_utils.move_furniture",
    defaultMessage: "Furniture and decorations may be moved",
  },
  noMoveFurniture: {
    id: "listing.production_utils.no_move_furniture",
    defaultMessage: "No moving furniture and decorations",
  },
  smokeMachine: {
    id: "listing.production_utils.smoke_machine",
    defaultMessage: "Smoke machines are allowed",
  },
  noSmokeMachine: {
    id: "listing.production_utils.no_smoke_machine",
    defaultMessage: "No smoke machines allowed",
  },
  nudity: {
    id: "listing.production_utils.nudity",
    defaultMessage: "Nudity is allowed",
  },
  noNudity: {
    id: "listing.production_utils.no_nudity",
    defaultMessage: "No nudity allowed",
  },
  sound: {
    id: "listing.production_utils.sound",
    defaultMessage: "Sound",
  },
  spaceAccess: {
    id: "listing.production_utils.space_access",
    defaultMessage: "Space access",
  },
  electrical: {
    id: "listing.production_utils.electrical",
    defaultMessage: "Electrical",
  },
  hostRules: {
    id: "listing.production_utils.host_rules",
    defaultMessage: "Host rules",
  },
});

const hasItem = (keys: string[], faqData: IListingRules) => {
  return some(keys, (question) => {
    const answer = faqData[question as keyof IListingRules];

    if (!has(faqData, question)) return false;
    if ("" === answer) return false;
    if (isNull(answer)) return false;
    if (isArray(answer) && 0 === answer.length) return false;

    return true;
  });
};

const accessTypeLabels = (answer: string[], intl: IntlShape) => {
  const keyByAccess = keyBy(accessTypes, "key");
  return map(answer, (item) => {
    const label = get(keyByAccess, [item, "label"]);
    return label && intl.formatMessage(label);
  });
};

const showFormError = (errors: { error: string }) => {
  const errorKey = get(Object.keys(errors), "0");

  if (!errorKey || "_error" === errorKey) {
    return;
  }

  return errors;
};

const lightFAQ = (answer: string) => ({
  has_natural_light: {
    label: answer
      ? definedMessages.naturalLight
      : definedMessages.noNaturalLight,
  },
  can_block_out_light: {
    label: answer
      ? definedMessages.blockNaturalLight
      : definedMessages.noBlockNaturalLight,
  },
  lighting_description: {
    label: definedMessages.lightingDescription,
    description: answer,
  },
});

const soundFAQ = (answer: string) => ({
  has_soundproofing: {
    label: answer ? definedMessages.soundProof : definedMessages.noSoundProof,
  },
  ambient_noise_description: {
    label: definedMessages.ambientNoiseDescription,
    description: answer,
  },
});

const accessFAQ = (answer: string[], intl: IntlShape) => ({
  space_access_types: {
    label: definedMessages.crewAccess,
    description: accessTypeLabels(answer, intl).join(", "),
  },
  equipment_loading_description: {
    label: definedMessages.equipmentLoadingDescription,
    description: answer,
  },
  has_separate_crew_area: {
    label: answer ? definedMessages.staging : definedMessages.noStaging,
  },
  separate_crew_area_description: {
    description: answer,
  },
  has_off_limit_areas: {
    label: answer ? definedMessages.offLimit : definedMessages.noOffLimit,
  },
  off_limit_areas_description: {
    description: answer,
  },
  can_film_exterior: {
    label: answer
      ? definedMessages.filmExterior
      : definedMessages.noFilmExterior,
  },
});

const electricalFAQ = (answer: string) => ({
  electrical_system_description: {
    label: definedMessages.electricalSystemDescription,
    description: answer,
  },
  can_access_circuit_breaker: {
    label: answer
      ? definedMessages.accessCircuitBreaker
      : definedMessages.noAccessCircuitBreaker,
  },
});

export const rulesFAQ = (answer: number, urlPrefix: string) => {
  return {
    rules: {
      label: definedMessages.rules,
      description: answer,
    },
    min_age: {
      label:
        0 < answer
          ? definedMessages.minAgeAllowed
          : definedMessages.allAgeAllowed,
    },
    requires_production_insurance: {
      label: answer
        ? definedMessages.prodInsuranceRequired
        : definedMessages.noProdInsuranceRequired,
    },
    insurance_requirement_description: {
      description: answer,
    },
    can_move_furniture: {
      label: answer
        ? definedMessages.moveFurniture
        : definedMessages.noMoveFurniture,
    },
    furniture_move_description: {
      description: answer,
    },
    can_use_smoke_machines: {
      label: answer
        ? definedMessages.smokeMachine
        : definedMessages.noSmokeMachine,
    },
    nudity_allowed: {
      label: answer ? definedMessages.nudity : definedMessages.noNudity,
    },
    has_surveillance: {
      label:
        answer &&
        defineMessages({
          has_surveillance: {
            id: "listing.listing_main.has_surveillance",
            defaultMessage: "Security cameras and recording devices",
          },
        }).has_surveillance,
    },
    surveillance_description: {
      description: answer && (
        <div>
          <div className="tw-mb-24 tw--mt-12">{answer}</div>
          <div>
            <FormattedMessage
              id="listing.production_utils.recording_device"
              defaultMessage="Recording devices in bathrooms or dressing rooms are prohibited by the {link}."
              values={{
                link: (
                  <a
                    href={`${urlPrefix ? `/${urlPrefix}` : ""}/terms`}
                    className="tw-cursor-pointer tw-text-gray-700"
                  >
                    <FormattedMessage
                      id="listing.production_utils.agreement"
                      defaultMessage="Peerspace Services Agreement"
                    />
                  </a>
                ),
              }}
            />
          </div>
        </div>
      ),
    },
  };
};

const productionQuestions = {
  lighting: {
    label: definedMessages.lighting,
    icon: "lighting",
    keys: ["has_natural_light", "can_block_out_light", "lighting_description"],
    faq: lightFAQ,
  },
  sound: {
    label: definedMessages.sound,
    icon: "sound",
    keys: ["has_soundproofing", "ambient_noise_description"],
    faq: soundFAQ,
  },
  access: {
    label: definedMessages.spaceAccess,
    icon: "access",
    keys: [
      "space_access_types",
      "equipment_loading_description",
      "has_separate_crew_area",
      "separate_crew_area_description",
      "has_off_limit_areas",
      "off_limit_areas_description",
      "can_film_exterior",
    ],
    faq: accessFAQ,
  },
  electrical: {
    label: definedMessages.electrical,
    icon: "electrical",
    keys: ["can_access_circuit_breaker", "electrical_system_description"],
    faq: electricalFAQ,
  },
  rules: {
    label: definedMessages.hostRules,
    icon: "rules",
    keys: [
      "requires_production_insurance",
      "insurance_requirement_description",
      "can_move_furniture",
      "furniture_move_description",
      "can_use_smoke_machines",
      "nudity_allowed",
      "has_surveillance",
      "surveillance_description",
    ],
    faq: rulesFAQ,
  },
};

const parkingTypeLabels = (answer: string[], intl: IntlShape) => {
  const keyByParking = keyBy(parkingTypes, "key");
  return map(answer, (item) => {
    const label = get(keyByParking, [item, "label"]);
    return label && intl.formatMessage(label);
  });
};

const parkingMsgs = defineMessages({
  parkingLabel: { id: "listing.parking_faq.label", defaultMessage: "Parking" },
  types: { id: "listing.parking_faq.types", defaultMessage: "Parking options" },
  description: {
    id: "listing.parking_faq.description",
    defaultMessage: "Parking description",
  },
  availability: {
    id: "listing.parking_faq.is_available",
    defaultMessage: "No parking options at this space",
  },
});

const parkingFAQ = (answer: string[], intl: IntlShape) => {
  return {
    types: {
      label: answer && parkingMsgs.types,
      description: parkingTypeLabels(answer, intl).join(", "),
    },
    description: {
      label: answer && parkingMsgs.description,
      description: answer,
    },
    is_available: {
      label: !answer && parkingMsgs.availability,
    },
  };
};

const parkingQuestions = {
  parking: {
    label: parkingMsgs.parkingLabel,
    icon: "parking",
    keys: ["types", "description", "is_available"],
    faq: parkingFAQ,
  },
};

export {
  showFormError,
  productionQuestions,
  hasItem,
  parkingFAQ,
  parkingQuestions,
};
